import axios from "axios";
import toast from "react-hot-toast";

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_NEXT_PUBLIC_API_URL || "http://localhost:4200";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// List of public endpoints that should never include auth token
const publicEndpoints = [
  "/api/v1/hero/featured-collections",
  "/api/v1/products/best-selling",
  "/api/v1/collections",
  "/api/v1/reviews/featured",
  "/api/v1/reviews/product",
  "/api/v1/products",
  "/api/v1/newsletter",
  "/api/v1/contact",
  "/api/v1/announcements",
];

// Helper function to check if URL is a public endpoint
const isPublicEndpoint = (url: string): boolean => {
  return publicEndpoints.some((endpoint) => {
    // Check if URL starts with the endpoint (handles query params)
    if (url.startsWith(endpoint)) {
      return true;
    }
    // Also check without leading slash
    if (url.startsWith(endpoint.substring(1))) {
      return true;
    }
    return false;
  });
};

// Add request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Skip adding token for public endpoints
    if (config.url && isPublicEndpoint(config.url)) {
      return config;
    }

    // Skip if explicitly marked to skip (e.g., from token refresh retry)
    if ((config as any).__skipTokenInterceptor) {
      return config;
    }

    // If Authorization header is already set (e.g., from token refresh retry), don't overwrite it
    if (config.headers.Authorization) {
      return config;
    }

    // Get token from localStorage (via zustand store)
    if (typeof window !== "undefined") {
      const authData = localStorage.getItem("aarsa-auth");
      if (authData) {
        try {
          const parsed = JSON.parse(authData);
          if (parsed.state?.token) {
            config.headers.Authorization = `Bearer ${parsed.state.token}`;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // If error is 401, logout user
    if (error.response?.status === 401) {
      if (typeof window !== "undefined") {
        const authStore = await import("./store");
        authStore.useAuthStore.getState().logout();
        toast.error("Your session has expired. Please log in again.");
      }
    }
    return Promise.reject(error);
  }
);

// Products API
export const productsApi = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    category?: string;
    classification?: string;
    collection?: string;
    search?: string;
    minPrice?: number;
    maxPrice?: number;
  }) => {
    const response = await api.get("/api/v1/products", { params });
    // Backend wraps response in: { success, message, data: { total, products } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  getById: async (id: number) => {
    const response = await api.get(`/api/v1/products/${id}`);
    return response.data;
  },
  getBySlug: async (slug: string) => {
    const response = await api.get(`/api/v1/products/${slug}`);
    // Backend wraps response in: { success, message, data: {...} }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  getSimilar: async (slug: string, limit: number = 8) => {
    const response = await api.get(`/api/v1/products/${slug}/similar`, {
      params: { limit },
    });
    // Backend wraps response in: { success, message, data: { total, products } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  getBestSelling: async (limit: number = 10) => {
    const response = await api.get("/api/v1/products/best-selling", {
      params: { limit },
    });
    // Backend wraps response in: { success, message, data: { total, products } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  getFastSelling: async () => {
    // Note: This endpoint may need to be implemented in the backend
    // For now, using the main products endpoint
    const response = await api.get("/api/v1/products", { params: { page_size: 10 } });
    return response.data;
  },
  getByClassification: async (classification: string) => {
    const response = await api.get(`/api/v1/products/classification/${classification}`);
    return response.data;
  },
};

// Cart API
export const cartApi = {
  get: async (token?: string) => {
    const response = await api.get("/api/v1/cart", {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },
  addItem: async (item: any, token?: string) => {
    const response = await api.post(
      "/api/v1/cart",
      item,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },
  updateItem: async (itemId: string, quantity: number, token?: string) => {
    const response = await api.put(
      `/api/v1/cart/${itemId}`,
      { quantity },
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },
  removeItem: async (itemId: string, token?: string) => {
    const response = await api.delete(`/api/v1/cart/${itemId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },
  clear: async (token?: string) => {
    const response = await api.delete("/api/v1/cart", {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },
};

// Auth API
export const authApi = {
  login: async (email: string, password: string) => {
    const response = await api.post("/api/v1/auth/login", {
      email,
      password,
    });
    
    // Backend wraps response in: { success, message, data: { access_token, token_type, user } }
    // Extract the data field from the wrapped response
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return {
        ...wrappedResponse.data,
        message: wrappedResponse.message || "Login successful",
      };
    }
    // Fallback to direct response if not wrapped
    return wrappedResponse;
  },
  register: async (data: any) => {
    const response = await api.post("/api/v1/auth/register", data);
    return response.data;
  },
  getMe: async (token: string) => {
    const response = await api.get("/api/v1/auth/me", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  sendOTP: async (identifier: string, isPhone: boolean = false) => {
    const response = await api.post("/api/v1/auth/send-otp", {
      identifier,
      is_phone: isPhone,
    });
    return response.data;
  },
  verifyOTP: async (identifier: string, otpCode: string, isPhone: boolean = false) => {
    const response = await api.post("/api/v1/auth/verify-otp", {
      identifier,
      otp_code: otpCode,
      is_phone: isPhone,
    });
    // Backend wraps response in: { success, message, data: {...} }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return {
        ...wrappedResponse.data,
        message: wrappedResponse.message || "Login successful",
      };
    }
    return wrappedResponse;
  },
};

// Wishlist API
export const wishlistApi = {
  getAll: async (token: string) => {
    const response = await api.get("/api/v1/wishlist", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  add: async (productId: number, token: string) => {
    const response = await api.post(
      "/api/v1/wishlist/add",
      { product_id: productId },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  },
  remove: async (productId: number, token: string) => {
    const response = await api.delete(`/api/v1/wishlist/remove/${productId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  check: async (productId: number, token: string) => {
    const response = await api.get(`/api/v1/wishlist/check/${productId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

// Orders API
export const ordersApi = {
  getAll: async (token: string) => {
    const response = await api.get("/api/v1/orders", {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Backend wraps response in: { success, message, data: { total, orders } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  getById: async (orderId: number, token: string) => {
    const response = await api.get(`/api/v1/orders/${orderId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  cancel: async (orderId: number, token: string, reason?: string) => {
    const response = await api.post(
      `/api/v1/orders/${orderId}/cancel`,
      { reason },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  },
  requestRefund: async (orderId: number, token: string, reason?: string) => {
    const response = await api.post(
      `/api/v1/orders/${orderId}/refund`,
      { reason },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  },
};

// Reviews API
export const reviewsApi = {
  create: async (review: {
    product_id: number;
    order_id?: number;
    rating: number;
    title?: string;
    comment?: string;
  }, token: string) => {
    const response = await api.post("/api/v1/reviews", review, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  update: async (reviewId: number, review: {
    rating?: number;
    title?: string;
    comment?: string;
  }, token: string) => {
    const response = await api.put(`/api/v1/reviews/${reviewId}`, review, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  getByProduct: async (productId: number) => {
    const response = await api.get(`/api/v1/reviews/product/${productId}`);
    return response.data;
  },
  getByUser: async (token: string) => {
    const response = await api.get("/api/v1/reviews/me", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

// Collections API
export const collectionsApi = {
  getAll: async () => {
    const response = await api.get("/api/v1/collections");
    // Backend wraps response in: { success, message, data: { total, collections } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
};

// Categories API
export const categoriesApi = {
  getAll: async () => {
    const response = await api.get("/api/v1/categories");
    // Backend wraps response in: { success, message, data: { total, categories } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
};

// Customer Reviews API
export const customerReviewsApi = {
  getFeatured: async (limit: number = 6) => {
    const response = await api.get("/api/v1/reviews/featured", {
      params: { limit },
    });
    // Backend wraps response in: { success, message, data: { total, reviews } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  getByProductName: async (productName: string, limit: number = 5) => {
    const response = await api.get(`/api/v1/reviews/product/${encodeURIComponent(productName)}`, {
      params: { limit },
    });
    // Backend wraps response in: { success, message, data: { total, reviews } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  getCountByProductName: async (productName: string) => {
    const response = await api.get(`/api/v1/reviews/product/${encodeURIComponent(productName)}/count`);
    // Backend wraps response in: { success, message, data: { count } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
};

// Hero Featured Collections API
export const heroApi = {
  getFeaturedCollections: async () => {
    const response = await api.get("/api/v1/hero/featured-collections");
    // Backend wraps response in: { success, message, data: { total, collections } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
};

// Admin API
export const adminApi = {
  getDashboardStats: async (token: string) => {
    const response = await api.get("/api/v1/admin/dashboard", {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Backend wraps response in: { success, message, data: { ... } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  // Products
  getProducts: async (token: string, params?: any) => {
    const response = await api.get("/api/v1/admin/products", {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    // Backend wraps response in: { success, message, data: { total, products } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  getProduct: async (productId: number, token: string) => {
    const response = await api.get(`/api/v1/admin/products/${productId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Backend wraps response in: { success, message, data: { ... } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  createProduct: async (product: any, token: string) => {
    const response = await api.post("/api/v1/admin/products", product, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Backend wraps response in: { success, message, data: { ... } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  updateProduct: async (productId: number, product: any, token: string) => {
    const response = await api.put(`/api/v1/admin/products/${productId}`, product, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  toggleProductActive: async (productId: number, token: string) => {
    const response = await api.put(`/api/v1/admin/products/${productId}/toggle-active`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  togglePriceFrozen: async (productId: number, token: string) => {
    const response = await api.put(`/api/v1/admin/products/${productId}/toggle-price-frozen`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  deleteProduct: async (productId: number, token: string) => {
    const response = await api.delete(`/api/v1/admin/products/${productId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  // Product Images
  addProductImage: async (productId: number, imageData: { image_url: string; alt_text?: string; is_primary?: boolean; display_order?: number }, token: string) => {
    const response = await api.post(`/api/v1/admin/products/${productId}/images`, imageData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Backend wraps response in: { success, message, data: { ... } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  deleteProductImage: async (productId: number, imageId: number, token: string) => {
    const response = await api.delete(`/api/v1/admin/products/${productId}/images/${imageId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  setPrimaryImage: async (productId: number, imageId: number, token: string) => {
    const response = await api.put(`/api/v1/admin/products/${productId}/images/${imageId}/primary`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  // Product Variants
  getProductVariants: async (productId: number, token: string) => {
    const response = await api.get(`/api/v1/admin/products/${productId}/variants`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  createProductVariant: async (productId: number, variantData: any, token: string) => {
    const response = await api.post(`/api/v1/admin/products/${productId}/variants`, variantData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  updateProductVariant: async (productId: number, variantId: number, variantData: any, token: string) => {
    const response = await api.put(`/api/v1/admin/products/${productId}/variants/${variantId}`, variantData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  deleteProductVariant: async (productId: number, variantId: number, token: string) => {
    const response = await api.delete(`/api/v1/admin/products/${productId}/variants/${variantId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  // Variant Images
  addVariantImage: async (productId: number, variantId: number, imageData: any, token: string) => {
    const response = await api.post(`/api/v1/admin/products/${productId}/variants/${variantId}/images`, imageData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  updateVariantImage: async (productId: number, variantId: number, imageId: number, imageData: any, token: string) => {
    const response = await api.put(`/api/v1/admin/products/${productId}/variants/${variantId}/images/${imageId}`, imageData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  deleteVariantImage: async (productId: number, variantId: number, imageId: number, token: string) => {
    const response = await api.delete(`/api/v1/admin/products/${productId}/variants/${variantId}/images/${imageId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  // Inventory
  getInventory: async (token: string, params?: any) => {
    const response = await api.get("/api/v1/admin/inventory", {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return response.data;
  },
  updateInventory: async (productId: number, inventory: any, token: string) => {
    const response = await api.put(`/api/v1/admin/inventory/${productId}`, inventory, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  // Orders
  getOrders: async (token: string, params?: any) => {
    const response = await api.get("/api/v1/admin/orders", {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    // Backend wraps response in: { success, message, data: { total, orders } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  createOrder: async (
    orderData: {
      user_id: number;
      items: Array<{ product_id: number; quantity: number; price: number }>;
      subtotal: number;
      shipping: number;
      tax: number;
      discount: number;
      total_amount: number;
      delivery_name: string;
      delivery_phone: string;
      delivery_address_line1: string;
      delivery_address_line2?: string;
      delivery_city: string;
      delivery_state: string;
      delivery_pincode: string;
      delivery_country?: string;
      order_status?: string;
      payment_status?: string;
      payment_method?: string;
      razorpay_payment_id?: string;
    },
    token: string
  ) => {
    const response = await api.post("/api/v1/admin/orders", orderData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  getOrder: async (orderId: number, token: string) => {
    const response = await api.get(`/api/v1/admin/orders/${orderId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Backend wraps response in: { success, message, data: { ... } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  updateOrderStatus: async (orderId: number, status: string, token: string) => {
    const response = await api.put(
      `/api/v1/admin/orders/${orderId}/status`,
      { status },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  },
  processRefund: async (
    orderId: number,
    refundData: {
      refund_amount?: number;
      refund_reason?: string;
      refund_method?: string;
    },
    token: string
  ) => {
    const response = await api.post(
      `/api/v1/admin/orders/${orderId}/refund`,
      refundData,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  deleteOrder: async (orderId: number, token: string) => {
    const response = await api.delete(`/api/v1/admin/orders/${orderId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  // Payments
  getPayments: async (token: string, params?: any) => {
    const response = await api.get("/api/v1/admin/payments", {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    // Backend wraps response in: { success, message, data: { total, payments } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  updatePaymentStatus: async (paymentId: number, status: string, token: string) => {
    const response = await api.put(
      `/api/v1/admin/payments/${paymentId}/status`,
      { status: status },
      {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  },
  // Users
  getUsers: async (token: string, params?: any) => {
    const response = await api.get("/api/v1/admin/users", {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    // Backend wraps response in: { success, message, data: { total, users } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  createUser: async (user: any, token: string) => {
    const response = await api.post("/api/v1/admin/users", user, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Backend wraps response in: { success, message, data: { ... } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  deleteUser: async (userId: number, token: string) => {
    const response = await api.delete(`/api/v1/admin/users/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  // Wishlist
  getWishlistItems: async (token: string, params?: any) => {
    const response = await api.get("/api/v1/admin/wishlist", {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    // Backend wraps response in: { success, message, data: { total, wishlist_items } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  // Categories
  getCategory: async (categoryId: number, token: string) => {
    const response = await api.get(`/api/v1/admin/categories/${categoryId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  createCategory: async (category: any, token: string) => {
    const response = await api.post("/api/v1/admin/categories", category, {
      headers: { 
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  },
  updateCategory: async (categoryId: number, category: any, token: string) => {
    const response = await api.put(`/api/v1/admin/categories/${categoryId}`, category, {
      headers: { 
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  },
  deleteCategory: async (categoryId: number, token: string) => {
    const response = await api.delete(`/api/v1/admin/categories/${categoryId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  // Collections
  getCollections: async (token: string) => {
    const response = await api.get("/api/v1/admin/collections", {
      headers: { Authorization: `Bearer ${token}` },
    });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  getCollection: async (collectionId: number, token: string) => {
    const response = await api.get(`/api/v1/admin/collections/${collectionId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  createCollection: async (collection: any, token: string) => {
    const response = await api.post("/api/v1/admin/collections", collection, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  },
  updateCollection: async (collectionId: number, collection: any, token: string) => {
    const response = await api.put(`/api/v1/admin/collections/${collectionId}`, collection, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    return response.data;
  },
  deleteCollection: async (collectionId: number, token: string) => {
    const response = await api.delete(`/api/v1/admin/collections/${collectionId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  // Hero Featured Collections
  getHeroFeaturedCollections: async (token: string, params?: any) => {
    const response = await api.get("/api/v1/admin/hero/featured-collections", {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  getHeroFeaturedCollection: async (collectionId: number, token: string) => {
    const response = await api.get(`/api/v1/admin/hero/featured-collections/${collectionId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  createHeroFeaturedCollection: async (collection: any, token: string) => {
    const response = await api.post("/api/v1/admin/hero/featured-collections", collection, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  updateHeroFeaturedCollection: async (collectionId: number, collection: any, token: string) => {
    const response = await api.put(`/api/v1/admin/hero/featured-collections/${collectionId}`, collection, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  deleteHeroFeaturedCollection: async (collectionId: number, token: string) => {
    const response = await api.delete(`/api/v1/admin/hero/featured-collections/${collectionId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  // Announcements
  getAnnouncements: async (token: string) => {
    const response = await api.get("/api/v1/admin/announcements", {
      headers: { Authorization: `Bearer ${token}` },
    });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  getAnnouncement: async (announcementId: number, token: string) => {
    const response = await api.get(`/api/v1/admin/announcements/${announcementId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  createAnnouncement: async (announcement: any, token: string) => {
    const response = await api.post("/api/v1/admin/announcements", announcement, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  updateAnnouncement: async (announcementId: number, announcement: any, token: string) => {
    const response = await api.put(`/api/v1/admin/announcements/${announcementId}`, announcement, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  deleteAnnouncement: async (announcementId: number, token: string) => {
    const response = await api.delete(`/api/v1/admin/announcements/${announcementId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  // Sales Analytics
  getSalesAnalytics: async (startDate: string, endDate: string, token: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const response = await api.get(`/api/v1/admin/analytics/sales?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
};

// Storage API
export const storageApi = {
  getUploadUrl: async (data: {
    file_name: string;
    file_type: string;
    file_size: number;
    product_id?: number | null;
    category_id?: number | null;
  }) => {
    const response = await api.post("/api/v1/storage/upload-url", data);
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  uploadComplete: async (data: {
    s3_key: string;
    product_id?: number | null;
    category_id?: number | null;
    alt_text?: string | null;
  }) => {
    const response = await api.post("/api/v1/storage/upload-complete", data);
    return response.data;
  },
  deleteFile: async (s3_key: string) => {
    const response = await api.delete(`/api/v1/storage/${encodeURIComponent(s3_key)}`);
    return response.data;
  },
};

// Purchases API
export const purchasesApi = {
  getAll: async (token: string, params?: any) => {
    const response = await api.get("/api/v1/admin/purchases", {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    // Backend wraps response in: { success, message, data: { total, purchases } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  getPurchase: async (purchaseId: number, token: string) => {
    const response = await api.get(`/api/v1/admin/purchases/${purchaseId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Backend wraps response in: { success, message, data: { ... } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  createPurchase: async (purchase: any, token: string) => {
    const response = await api.post("/api/v1/admin/purchases", purchase, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Backend wraps response in: { success, message, data: { ... } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  updatePurchase: async (purchaseId: number, purchase: any, token: string) => {
    const response = await api.put(`/api/v1/admin/purchases/${purchaseId}`, purchase, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Backend wraps response in: { success, message, data: { ... } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  deletePurchase: async (purchaseId: number, token: string) => {
    const response = await api.delete(`/api/v1/admin/purchases/${purchaseId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  updateInvoice: async (purchaseId: number, invoiceUrl: string, token: string) => {
    const response = await api.put(`/api/v1/admin/purchases/${purchaseId}/invoice`, {
      invoice_url: invoiceUrl,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  toggleStatus: async (purchaseId: number, status: "used" | "unused", token: string) => {
    const response = await api.put(`/api/v1/admin/purchases/${purchaseId}/status`, {
      status: status,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
};

// Payments API
export const paymentsApi = {
  checkout: async (checkoutData: {
    items: Array<{
      product_id: number;
      quantity: number;
      price: number;
    }>;
    total_amount: number;
    shipping: number;
    tax: number;
    currency?: string;
    payment_method?: string; // "razorpay" | "cod" | "cash_on_delivery"
    delivery_name?: string;
    delivery_phone?: string;
    delivery_address_line1?: string;
    delivery_address_line2?: string;
    delivery_city?: string;
    delivery_state?: string;
    delivery_pincode?: string;
    delivery_country?: string;
    customer_name?: string;
    customer_email?: string;
    customer_phone?: string;
    is_gift?: boolean;
    gift_message?: string | null;
  }) => {
    const response = await api.post("/api/v1/payments/checkout", checkoutData);
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  verifyPayment: async (verificationData: {
    razorpay_order_id: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
    order_id: number;
  }) => {
    const response = await api.post("/api/v1/payments/verify", verificationData);
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
};

// Addresses API
export const addressesApi = {
  getAll: async () => {
    const response = await api.get("/api/v1/addresses");
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  create: async (addressData: {
    name: string;
    phone: string;
    address_line1: string;
    address_line2?: string;
    city: string;
    state: string;
    pincode: string;
    country?: string;
    is_default?: boolean;
  }) => {
    const response = await api.post("/api/v1/addresses", addressData);
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  update: async (addressId: number, addressData: {
    name?: string;
    phone?: string;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    state?: string;
    pincode?: string;
    country?: string;
    is_default?: boolean;
  }) => {
    const response = await api.put(`/api/v1/addresses/${addressId}`, addressData);
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  delete: async (addressId: number) => {
    await api.delete(`/api/v1/addresses/${addressId}`);
  },
};

// Coupons API
export const couponsApi = {
  getAll: async () => {
    const response = await api.get("/api/v1/coupons");
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  apply: async (code: string, orderAmount: number) => {
    const response = await api.post("/api/v1/coupons/apply", {
      code,
      order_amount: orderAmount,
    });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
};

// Admin Coupons API
export const adminCouponsApi = {
  getAll: async (token: string) => {
    const response = await api.get("/api/v1/admin/coupons", {
      headers: { Authorization: `Bearer ${token}` },
    });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  getCoupon: async (couponId: number, token: string) => {
    const response = await api.get(`/api/v1/admin/coupons/${couponId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  createCoupon: async (coupon: any, token: string) => {
    const response = await api.post("/api/v1/admin/coupons", coupon, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  updateCoupon: async (couponId: number, coupon: any, token: string) => {
    const response = await api.put(`/api/v1/admin/coupons/${couponId}`, coupon, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  deleteCoupon: async (couponId: number, token: string) => {
    await api.delete(`/api/v1/admin/coupons/${couponId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },
};

// Newsletter API
export const newsletterApi = {
  subscribe: async (email: string) => {
    const response = await api.post("/api/v1/newsletter/subscribe", { email });
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
};

// Contact API
export const contactApi = {
  submit: async (contactData: {
    name: string;
    email: string;
    subject: string;
    message: string;
  }) => {
    const response = await api.post("/api/v1/contact/submit", contactData);
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
};

export default api;

